import React, { useState } from 'react';
import {
  AccordionsItem,
  AccordionsItemHeader,
  AccordionsItemSection,
  Badge,
  Button,
  CondensedLarge,
  DateUtil,
  EmptyLayout,
  Flashy,
  FluidButtonLayout,
  Form,
  FormLabel,
  Gutter,
  Icon,
  IconButton,
  Layout,
  LayoutItem,
  ReadonlyTextarea,
  Textarea,
} from '@axiom/ui';
import type {
  Candidate,
  AhqSubmissionCandidateExperience,
} from '@axiom/validation';
import { AhqSubmissionCandidateExperienceSchema } from '@axiom/validation';

import EmptyProject from '../../public/svg/empty-project.svg';

const editSubmissionDescriptionFormSchema =
  AhqSubmissionCandidateExperienceSchema.pick({
    submissionDescription: true,
  });

export type OpportunitySubmissionCandidateExperienceItemProps = {
  experience: AhqSubmissionCandidateExperience;
  readOnly?: boolean;
  onEyeballClicked: () => Promise<void>;
  onStarClicked?: () => Promise<void>;
  onExperienceSubmissionDescriptionChanged: (
    submissionDescription: string | null,
    experienceId: AhqSubmissionCandidateExperience['id']
  ) => Promise<void>;
  candidateId: Candidate['id'];
  eyeOpen?: boolean;
  isHighlighted?: boolean;
};
export const OpportunitySubmissionCandidateExperienceItem = ({
  experience,
  readOnly = false,
  onEyeballClicked,
  onStarClicked,
  onExperienceSubmissionDescriptionChanged,
  eyeOpen = false,
  isHighlighted = false,
}: OpportunitySubmissionCandidateExperienceItemProps) => {
  const [editDescription, setEditDescription] = useState(false);
  const toggleEditDescription = () => {
    setEditDescription(!editDescription);
  };
  const hasSubmissionDescription =
    (experience.submissionDescription ?? null) !== null;
  const currentDescription = hasSubmissionDescription
    ? experience.submissionDescription
    : experience.description;
  const descriptionContent = currentDescription ? (
    <ReadonlyTextarea
      name="READONLY_EXPERIENCE_DESCRIPTION"
      value={currentDescription}
      formatting="modern"
    />
  ) : (
    <EmptyLayout
      graphic={<EmptyProject width="117px" height="117px" />}
      name="NO_EXPERIENCE_DESCRIPTION"
    >
      <Layout position="middle" stretched>
        <LayoutItem>
          <em>No description</em>
        </LayoutItem>
      </Layout>
    </EmptyLayout>
  );

  return (
    <AccordionsItem name="EXPERIENCE_ITEM" data-value={experience.id}>
      <AccordionsItemHeader>
        <Layout position="middle space-between" horizontalGutter="16px">
          {experience.isAxiom ? (
            <Badge background="orange" name="AXIOM_EXPERIENCE_BADGE">
              A
            </Badge>
          ) : null}
          <LayoutItem fluid>
            <CondensedLarge name="EXPERIENCE_STRING">
              <Flashy bold name="CLIENT">
                {`${experience.client}; `}
              </Flashy>
              <span data-test="START">
                {DateUtil.displayLongMonthYear(experience.startDate)}
              </span>
              {' - '}
              <span data-test="END">
                {DateUtil.displayEndDate(experience.endDate)}
              </span>
            </CondensedLarge>
          </LayoutItem>
          {hasSubmissionDescription &&
          experience.submissionDescription !== experience.description ? (
            <Badge
              background="gray"
              name="EXPERIENCE_SUBMISSION_DESCRIPTION_EDITED_BADGE"
            >
              Edited
            </Badge>
          ) : null}
          {readOnly ? null : (
            <>
              {eyeOpen ? (
                <IconButton
                  name="EXPERIENCE_STAR_ICON"
                  icon={isHighlighted ? 'star-filled' : 'star'}
                  variation="minimal"
                  pattern="secondary"
                  onClick={async e => {
                    e.stopPropagation();
                    await onStarClicked?.();
                  }}
                />
              ) : null}
              <IconButton
                name="EXPERIENCE_EYEBALL_ICON"
                icon={eyeOpen ? 'eye' : 'eye-hidden'}
                variation="minimal"
                pattern="secondary"
                onClick={async e => {
                  e.stopPropagation();
                  await onEyeballClicked();
                }}
              />
            </>
          )}
          <Flashy color="contentBreak">
            <Icon name="spacer-vertical" />
          </Flashy>
          <Gutter right="8px" />
        </Layout>
      </AccordionsItemHeader>
      <AccordionsItemSection>
        <Gutter vertical="18px">
          <Layout position="middle space-between">
            {editDescription ? null : <FormLabel>Description</FormLabel>}
            {!editDescription && !readOnly ? (
              <IconButton
                icon="pencil"
                pattern="secondary"
                variation="minimal"
                onClick={toggleEditDescription}
                name="EDIT_DESCRIPTION_BUTTON"
              />
            ) : null}
          </Layout>
          {editDescription ? (
            <Form
              name="EDIT_EXPERIENCE_DESCRIPTION"
              schema={editSubmissionDescriptionFormSchema}
              initialValues={{
                submissionDescription: currentDescription,
              }}
              onSubmit={async (changedData, actions, values) => {
                await onExperienceSubmissionDescriptionChanged(
                  values.submissionDescription,
                  experience.id
                );
                toggleEditDescription();
              }}
            >
              {({ fireSubmit }) => {
                return (
                  <>
                    <Textarea
                      name="submissionDescription"
                      id={`experience-${experience.id}-submissionDescription`}
                      label="Description"
                      full
                      formatting="modern"
                      enableSuggestionsFor="envoy-OpportunitySubmissionCandidateExperienceItem-submissionDescription"
                    />
                    <Gutter bottom="24px" />
                    <FluidButtonLayout>
                      <Button
                        name="CANCEL_EXPERIENCE_DESCRIPTION"
                        onClick={toggleEditDescription}
                        variation="outline"
                      >
                        Cancel
                      </Button>
                      <Button
                        name="SAVE_EXPERIENCE_DESCRIPTION"
                        type="submit"
                        onClick={fireSubmit}
                      >
                        Save
                      </Button>
                    </FluidButtonLayout>
                  </>
                );
              }}
            </Form>
          ) : (
            descriptionContent
          )}
        </Gutter>
      </AccordionsItemSection>
    </AccordionsItem>
  );
};
